/**
 * Unit identity helpers shared by client and server.
 *
 * The condominium's real nomenclature is <building letter>-<number> (E-1 … R-8). The
 * board asked for a standard, visible unit code so every owner can be identified
 * without ambiguity across the portal, notices and the property registry. The code
 * is derived from the unit number (and building, when the number alone is bare) and
 * stored on the unit; an admin may override it.
 */
export const UNIT_CODE_PREFIX = "CD1";

/** Trim, uppercase and drop inner whitespace: " e-1 " → "E-1", "j 5" → "J5". */
export function normalizeUnitNumber(raw: string): string {
  return (raw ?? "").trim().toUpperCase().replace(/\s+/g, "");
}

/**
 * Standard code for a unit: `CD1-<BUILDING>-<NN>`.
 *   "E-1" / "E1"      → CD1-E-01
 *   "J5"              → CD1-J-05
 *   "R-12"            → CD1-R-12
 *   "10" + building B → CD1-B-10
 * Anything unparseable keeps the cleaned number behind the prefix (CD1-PH-A).
 */
export function deriveUnitCode(unitNumber: string, building?: string | null): string | null {
  const n = normalizeUnitNumber(unitNumber);
  if (!n) return null;
  let m = n.match(/^([A-Z]{1,3})[-–]?(\d{1,3})([A-Z]?)$/);
  if (m) return `${UNIT_CODE_PREFIX}-${m[1]}-${m[2].padStart(2, "0")}${m[3]}`;
  const b = normalizeUnitNumber(building ?? "").replace(/[^A-Z0-9]/g, "");
  m = n.match(/^(\d{1,3})[-–]?([A-Z]?)$/);
  if (m && b) return `${UNIT_CODE_PREFIX}-${b}-${m[1].padStart(2, "0")}${m[2]}`;
  return `${UNIT_CODE_PREFIX}-${n.replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
}
